<template>
  <v-card elevation="0" rounded="lg" class="border">
    <v-card-text>
      <!-- Which metric the chart shows: the shared switch, tinted like the Design tab's sections; scrolls sideways on phones. -->
      <div class="d-flex justify-end mb-8">
        <segmented-toggle v-model="activeTab" :items="tabItems" label="Chart metric" small tint
          :scrollable="$vuetify.display.xs" />
      </div>
      <v-window v-model="activeTab">
        <v-window-item value="views" eager>
          <v-responsive :aspect-ratio="3" class="py-2">
            <canvas ref="viewsChart" height="220"></canvas>
          </v-responsive>
        </v-window-item>
        <v-window-item value="leads" eager>
          <v-responsive :aspect-ratio="3" class="py-2">
            <canvas ref="leadsChart" height="220"></canvas>
          </v-responsive>
        </v-window-item>
        <v-window-item value="partial_leads" eager>
          <v-responsive :aspect-ratio="3" class="py-2">
            <canvas ref="partialLeadsChart" height="220"></canvas>
          </v-responsive>
        </v-window-item>
        <v-window-item value="conversion_rate" eager>
          <v-responsive :aspect-ratio="3" class="py-2">
            <canvas ref="conversionRateChart" height="220"></canvas>
          </v-responsive>
        </v-window-item>
      </v-window>
    </v-card-text>
  </v-card>
</template>
<script>
import Chart from 'chart.js'
import SegmentedToggle from '@/components/common/SegmentedToggle.vue'
import { METRIC_CARD_CONFIG } from '@/mixins/dashboard'
const CHART_UPDATE_DURATION_MS = 0
export const GRAPH_GAP_MS = 200
const DEFAULT_TAB = 'views'
// The metrics the chart can show; their labels and icons come from the stat cards above it.
const CHART_METRICS = ['views', 'leads', 'partial_leads', 'conversion_rate']
const TAB_ITEMS = METRIC_CARD_CONFIG
  .filter(metric => CHART_METRICS.includes(metric.key))
  .map(metric => ({ value: metric.key, text: metric.title, icon: metric.icon }))
const CHART_BG_OPACITY = 0.1
const DATASET_LABELS = Object.freeze({
  CURRENT: 'Current Period',
  PREVIOUS: 'Previous Period'
})
const COLOR_TOKENS = Object.freeze({
  CURRENT: {
    HEX: '#2e5090',
    RGB: '46, 80, 144'
  },
  COMPARE: {
    HEX: '#e87722',
    RGB: '232, 119, 34'
  }
})
const rgba = (rgb, opacity) => `rgba(${rgb}, ${opacity})`
const METRIC_VALUE_RESOLVERS = Object.freeze({
  conversion_rate: (item) => item.conversion_rate
})
const CHART_STYLE = {
  CURRENT: {
    BORDER: COLOR_TOKENS.CURRENT.HEX,
    BG: rgba(COLOR_TOKENS.CURRENT.RGB, CHART_BG_OPACITY)
  },
  COMPARE: {
    BORDER: COLOR_TOKENS.COMPARE.HEX,
    BG: rgba(COLOR_TOKENS.COMPARE.RGB, CHART_BG_OPACITY)
  },
  BORDER_WIDTH: 2,
  TENSION: 0.4,
  X_TICKS: {
    AUTO_SKIP_PADDING: 50,
    MAX_TICKS_LIMIT: 6,
    MAX_ROTATION: 0,
    MIN_ROTATION: 0
  }
}
const KNOWN_SERIES_LABEL_KEYS = Object.freeze([
  'stat_date',
  'date',
  'created_at',
  'visits_created_at',
  'leads_created_at',
  'partial_leads_created_at',
  'conversion_rate_created_at',
  'completion_time_created_at'
])
export default {
  name: 'DashboardGraph',
  components: { SegmentedToggle },
  props: {
    metrics: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeTab: DEFAULT_TAB,
      tabItems: TAB_ITEMS,
      charts: {
        views: null,
        leads: null,
        partial_leads: null,
        conversion_rate: null
      },
      chartRefMap: {
        views: 'viewsChart',
        leads: 'leadsChart',
        partial_leads: 'partialLeadsChart',
        conversion_rate: 'conversionRateChart'
      }
    }
  },
  watch: {
    activeTab(newTab) {
      if (!newTab) {
        this.activeTab = DEFAULT_TAB
        return
      }
      this.$nextTick(() => {
        this.renderOrRefreshChart(newTab)
      })
    }
  },
  methods: {
    getSeriesMapping(metric, sample = {}) {
      const metricKeyMap = {
        views: { label: 'visits_created_at', value: 'visits' },
        leads: { label: 'leads_created_at', value: 'leads' },
        partial_leads: { label: 'partial_leads_created_at', value: 'partial_leads' },
        conversion_rate: { label: 'conversion_rate_created_at', value: 'conversion_rate' },
        completion_time: { label: 'completion_time_created_at', value: 'completion_time' }
      }
      let labelKey = metricKeyMap[metric]?.label
      let valueKey = metricKeyMap[metric]?.value
      if (!sample || typeof sample !== 'object') {
        return { labelKey, valueKey }
      }
      if (!labelKey || !(labelKey in sample)) {
        labelKey = KNOWN_SERIES_LABEL_KEYS.find(k => k in sample) || labelKey
      }
      if (!valueKey || !(valueKey in sample)) {
        valueKey = ['total', 'value'].find(k => k in sample) || valueKey
      }
      return { labelKey, valueKey }
    },
    buildCompareDataset() {
      return {
        label: DATASET_LABELS.PREVIOUS,
        data: [],
        borderColor: CHART_STYLE.COMPARE.BORDER,
        backgroundColor: CHART_STYLE.COMPARE.BG,
        fill: true,
        tension: CHART_STYLE.TENSION,
        borderWidth: CHART_STYLE.BORDER_WIDTH
      }
    },
    renderOrRefreshChart(metric) {
      const canvasRef = this.chartRefMap?.[metric] || `${metric}Chart`
      const canvas = this.$refs[canvasRef]
      if (this.charts?.[metric] && canvas && this.charts[metric].ctx?.canvas !== canvas) {
        try {
          this.charts[metric].destroy()
        } catch (e) {
          console.warn('[Dashboard] chart destroy failed', metric, e)
        }
        this.charts[metric] = null
      }
      if (this.charts?.[metric]) {
        this.updateChart(metric)
        this.$nextTick(() => {
          try {
            this.charts?.[metric]?.resize()
            this.charts?.[metric]?.update(CHART_UPDATE_DURATION_MS)
          } catch (e) {
            console.warn('[Dashboard] chart resize/update failed', metric, e)
          }
        })
        return
      }
      this.renderChart(metric)
    },
    extractChartData(metricData) {
      const baseData = metricData.value || metricData.data?.value || []
      const compareData = metricData.compare?.value || metricData.compare_value || []
      return { baseData, compareData }
    },
    extractChartValues(data, labelKey, valueKey, metric) {
      const labels = data.map((item, index) => this.resolveLabel(item, index, labelKey))
      const values = data.map(item => this.resolveMetricValue(item, { metric, valueKey }))
      return { labels, values }
    },
    resolveLabel(item, index, labelKey) {
      return (labelKey && item[labelKey]) || item.stat_date || item.date || item.created_at || `Unit ${index + 1}`
    },
    resolveMetricValue(item, { metric, valueKey }) {
      const resolved = METRIC_VALUE_RESOLVERS[metric]?.(item)
      const keyedValue = valueKey ? item[valueKey] : undefined
      return resolved ?? keyedValue ?? item.total ?? item.value ?? 0
    },
    sortLabels(labels = []) {
      return [...labels].sort((a, b) => {
        const timeA = Date.parse(a)
        const timeB = Date.parse(b)
        const hasTimeA = Number.isFinite(timeA)
        const hasTimeB = Number.isFinite(timeB)
        if (hasTimeA && hasTimeB) return timeA - timeB
        return String(a).localeCompare(String(b))
      })
    },
    alignSeriesByLabels(baseLabels, baseValues, compareLabels, compareValues) {
      const merged = new Set([...(baseLabels || []), ...(compareLabels || [])])
      const labels = this.sortLabels(Array.from(merged).filter(Boolean))
      const baseMap = new Map((baseLabels || []).map((label, index) => [label, baseValues[index]]))
      const compareMap = new Map((compareLabels || []).map((label, index) => [label, compareValues[index]]))
      return {
        labels,
        baseValues: labels.map(label => (baseMap.has(label) ? baseMap.get(label) : null)),
        compareValues: labels.map(label => (compareMap.has(label) ? compareMap.get(label) : null))
      }
    },
    ensurePrimaryDataset(chart) {
      if (chart.data.datasets?.[0]) return chart.data.datasets[0]
      chart.data.datasets = [{ label: DATASET_LABELS.CURRENT, data: [] }]
      return chart.data.datasets[0]
    },
    ensureCompareDataset(chart) {
      if (chart.data.datasets?.[1]) return chart.data.datasets[1]
      const compareDataset = this.buildCompareDataset()
      chart.data.datasets.push(compareDataset)
      return compareDataset
    },
    applyCompareDatasetStyle(compareDataset) {
      compareDataset.borderColor = CHART_STYLE.COMPARE.BORDER
      compareDataset.backgroundColor = CHART_STYLE.COMPARE.BG
      compareDataset.fill = true
      compareDataset.tension = CHART_STYLE.TENSION
      compareDataset.borderWidth = CHART_STYLE.BORDER_WIDTH
    },
    syncCompareDataset(chart, compareValues) {
      if (compareValues.length === 0) {
        if (chart.data.datasets.length > 1) {
          chart.data.datasets.splice(1, chart.data.datasets.length - 1)
        }
        return
      }
      const compareDataset = this.ensureCompareDataset(chart)
      compareDataset.label = DATASET_LABELS.PREVIOUS
      compareDataset.data = compareValues
      this.applyCompareDatasetStyle(compareDataset)
    },
    renderChart(metric) {
      const metricData = this.metrics?.[metric]
      if (!metricData) return
      const canvasRef = this.chartRefMap?.[metric] || `${metric}Chart`
      const canvas = this.$refs[canvasRef]
      if (!canvas) return
      const canvasCtx = canvas.getContext && canvas.getContext('2d')
      if (!canvasCtx) return
      const { baseData, compareData } = this.extractChartData(metricData)
      const sample = baseData[0] || compareData[0] || {}
      const { labelKey, valueKey } = this.getSeriesMapping(metric, sample)
      const { labels: baseLabels, values: baseSeriesValues } = this.extractChartValues(baseData, labelKey, valueKey, metric)
      const { labels: compareLabels, values: compareSeriesValues } = this.extractChartValues(compareData, labelKey, valueKey, metric)
      const { labels, baseValues, compareValues } = this.alignSeriesByLabels(
        baseLabels,
        baseSeriesValues,
        compareLabels,
        compareSeriesValues
      )
      const datasets = [
        {
          label: DATASET_LABELS.CURRENT,
          data: baseValues,
          borderColor: CHART_STYLE.CURRENT.BORDER,
          backgroundColor: CHART_STYLE.CURRENT.BG,
          fill: true,
          tension: CHART_STYLE.TENSION,
          borderWidth: CHART_STYLE.BORDER_WIDTH
        }
      ]
      // Only include compare line if we have compare values
      if (compareValues.length > 0) {
        const compareDataset = this.buildCompareDataset()
        compareDataset.data = compareValues
        datasets.push(compareDataset)
      }
      this.charts[metric] = new Chart(canvasCtx, {
        type: 'line',
        data: {
          labels,
          datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          scales: {
            xAxes: [{
              ticks: {
                autoSkip: true,
                autoSkipPadding: CHART_STYLE.X_TICKS.AUTO_SKIP_PADDING,
                maxTicksLimit: CHART_STYLE.X_TICKS.MAX_TICKS_LIMIT,
                maxRotation: CHART_STYLE.X_TICKS.MAX_ROTATION,
                minRotation: CHART_STYLE.X_TICKS.MIN_ROTATION
              }
            }],
            yAxes: [{
              ticks: {
                beginAtZero: true
              }
            }]
          },
          legend: {
            display: true,
            position: 'top'
          }
        }
      })
    },
    updateChart(metric) {
      const metricData = this.metrics?.[metric]
      const chart = this.charts?.[metric]
      if (!metricData || !chart) return
      const { baseData, compareData } = this.extractChartData(metricData)
      const sample = baseData[0] || compareData[0] || {}
      const { labelKey, valueKey } = this.getSeriesMapping(metric, sample)
      const { labels: baseLabels, values: baseSeriesValues } = this.extractChartValues(baseData, labelKey, valueKey, metric)
      const { labels: compareLabels, values: compareSeriesValues } = this.extractChartValues(compareData, labelKey, valueKey, metric)
      const { labels, baseValues, compareValues } = this.alignSeriesByLabels(
        baseLabels,
        baseSeriesValues,
        compareLabels,
        compareSeriesValues
      )
      chart.data.labels = labels
      const primaryDataset = this.ensurePrimaryDataset(chart)
      primaryDataset.label = DATASET_LABELS.CURRENT
      primaryDataset.data = baseValues
      this.syncCompareDataset(chart, compareValues)
    }
  },
  beforeUnmount() {
    Object.keys(this.charts).forEach(key => {
      if (this.charts[key]) {
        this.charts[key].destroy()
      }
    })
  }
}
</script>
